// ============================================================================
// ABILITY SYSTEM - Hero ability implementations
// ============================================================================

import { EntityId, Vector2, HeroId, Team } from '@/shared/types';
import { Vec2, generateId } from '@/shared/utils';
import { Player } from '@/game/entities/Player';
import { 
  EffectSystem, 
  EffectType, 
  ZoneVisualType,
  ABILITY_RANGES,
  tilesToPixels 
} from './EffectSystem';

// ----------------------------------------------------------------------------
// ABILITY RESULT TYPES
// ----------------------------------------------------------------------------

export interface AbilityResult {
  success: boolean;
  effectsApplied: string[];
  damage?: number;
  healing?: number;
  targetIds?: EntityId[];
  projectiles?: ProjectileData[];
  zones?: ZoneData[];
  teleportTo?: Vector2;
  visualEffects?: VisualEffectData[];
}

export interface ProjectileData {
  id: string;
  ownerId: EntityId;
  position: Vector2;
  direction: Vector2;
  speed: number;
  damage: number;
  lifetime: number;
  isHoming?: boolean;
  targetId?: EntityId;
}

export interface ZoneData {
  id: string;
  type: ZoneVisualType;
  position: Vector2;
  radius: number;
  duration: number;
}

export interface VisualEffectData {
  type: VisualEffectType;
  position: Vector2;
  direction?: number;
  radius?: number;
  duration: number;
  color?: string;
  targetPosition?: Vector2;
  followPlayerId?: string; // If set, effect follows this player's position/rotation
}

export enum VisualEffectType {
  Blink = 'blink',
  BlinkTrail = 'blink_trail',
  Shockwave = 'shockwave',
  Shield = 'shield',
  DomeShield = 'dome_shield',
  BarrierWall = 'barrier_wall',
  Cloak = 'cloak',
  Reveal = 'reveal',
  HealBurst = 'heal_burst',
  HealingField = 'healing_field',
  PoisonCloud = 'poison_cloud',
  Grenade = 'grenade',
  Explosion = 'explosion',
  TurretSpawn = 'turret_spawn',
  DashSlash = 'dash_slash',
  MinigunMode = 'minigun_mode',
}

// ----------------------------------------------------------------------------
// TURRET ENTITY
// ----------------------------------------------------------------------------

export interface Turret {
  id: string;
  ownerId: EntityId;
  ownerTeam: Team;
  position: Vector2;
  rotation: number;
  health: number;
  maxHealth: number;
  range: number;
  damage: number;
  fireRate: number;
  lastFireTime: number;
  spawnTime: number;
  duration: number;
  targetId: EntityId | null;
}

// ----------------------------------------------------------------------------
// ABILITY SYSTEM
// ----------------------------------------------------------------------------

export class AbilitySystem {
  private effectSystem: EffectSystem;
  private turrets: Map<string, Turret> = new Map();
  private activeMiniguns: Map<EntityId, number> = new Map(); // playerId -> endTime
  
  // Callbacks for game integration
  private getEnemies: (team: Team) => Player[];
  private getAllies: (team: Team, excludeId?: EntityId) => Player[];
  private getTime: () => number;
  private raycast: (start: Vector2, direction: Vector2, maxDist: number, ignorePlayers?: EntityId[]) => { point: Vector2; distance: number } | null;

  constructor(
    effectSystem: EffectSystem,
    callbacks: {
      getPlayer: (id: EntityId) => Player | undefined;
      getPlayers: () => Player[];
      getEnemies: (team: Team) => Player[];
      getAllies: (team: Team, excludeId?: EntityId) => Player[];
      getTime: () => number;
      raycast: (start: Vector2, direction: Vector2, maxDist: number, ignorePlayers?: EntityId[]) => { point: Vector2; distance: number } | null;
    }
  ) {
    this.effectSystem = effectSystem;
    this.getEnemies = callbacks.getEnemies;
    this.getAllies = callbacks.getAllies;
    this.getTime = callbacks.getTime;
    this.raycast = callbacks.raycast;
  }

  // -------------------------------------------------------------------------
  // MAIN ABILITY EXECUTION
  // -------------------------------------------------------------------------

  executeAbility(player: Player, abilityIndex: number): AbilityResult {
    // Check if silenced
    if (this.effectSystem.isSilenced(player.id)) {
      return { success: false, effectsApplied: [] };
    }

    switch (player.heroId) {
      case HeroId.Katana: // Phantom / Assassin
        return this.executePhantomAbility(player, abilityIndex);
      
      case HeroId.Vanta: // Titan / Tank
        return this.executeTitanAbility(player, abilityIndex);
      
      case HeroId.Spectre: // Spectre / Recon
        return this.executeSpectreAbility(player, abilityIndex);
      
      case HeroId.Mira: // Medic / Support
        return this.executeMedicAbility(player, abilityIndex);
      
      case HeroId.Spark: // Havoc / Assault
        return this.executeHavocAbility(player, abilityIndex);
      
      case HeroId.Byte: // Cipher / Tech
        return this.executeCipherAbility(player, abilityIndex);
      
      case HeroId.Glyph: // Venom / Saboteur
        return this.executeVenomAbility(player, abilityIndex);
      
      case HeroId.Zero: // Aegis / Guardian
        return this.executeAegisAbility(player, abilityIndex);
      
      default:
        return { success: false, effectsApplied: [] };
    }
  }

  // -------------------------------------------------------------------------
  // PHANTOM (Katana) - Assassin
  // Passive: Wall phase | Q: Blink | E: Invisibility
  // -------------------------------------------------------------------------

  private executePhantomAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: Wall Phase - handled in movement system
        break;

      case 1: // Q: Blink - teleport 3 tiles forward
        {
          const blinkDistance = ABILITY_RANGES.BLINK;
          const direction = Vec2.fromAngle(player.rotation);
          const targetPos = Vec2.add(player.position, Vec2.mul(direction, blinkDistance));
          
          // Check for wall collision along the path
          const hit = this.raycast(player.position, direction, blinkDistance, [player.id]);
          const finalPos = hit ? Vec2.sub(hit.point, Vec2.mul(direction, 20)) : targetPos;
          
          result.teleportTo = finalPos;
          result.visualEffects!.push(
            { type: VisualEffectType.Blink, position: player.position, duration: 300 },
            { type: VisualEffectType.Blink, position: finalPos, duration: 300 },
            { type: VisualEffectType.BlinkTrail, position: player.position, targetPosition: finalPos, duration: 200 }
          );
          result.success = true;
        }
        break;

      case 2: // F: Invisibility - 4 seconds cloak
        {
          const duration = 4000;
          this.effectSystem.applyEffect(
            EffectType.Cloak,
            player.id,
            player.id,
            duration,
            100 // Full invisibility
          );
          result.effectsApplied.push('cloak');
          result.visualEffects!.push({
            type: VisualEffectType.Cloak,
            position: player.position,
            duration
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Shadow Strike - teleport behind target + damage
        {
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          if (enemies.length === 0) break;
          
          // Find closest visible enemy
          let target: Player | null = null;
          let closestDist = Infinity;
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist < closestDist && dist < tilesToPixels(10)) {
              closestDist = dist;
              target = enemy;
            }
          }
          
          if (target) {
            // Teleport behind target
            const behindDir = Vec2.fromAngle(target.rotation + Math.PI);
            const behindPos = Vec2.add(target.position, Vec2.mul(behindDir, 40));
            
            result.teleportTo = behindPos;
            result.damage = 75;
            result.targetIds = [target.id];
            result.visualEffects!.push(
              { type: VisualEffectType.Blink, position: player.position, duration: 300 },
              { type: VisualEffectType.DashSlash, position: behindPos, direction: target.rotation, duration: 500 }
            );
            result.success = true;
          }
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // TITAN (Vanta) - Tank
  // Passive: Spawn shield | Q: Fortify | E: Shockwave
  // -------------------------------------------------------------------------

  private executeTitanAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: +50 shield on spawn - handled in spawn logic
        break;

      case 1: // Q: Energy Shield - Front-facing barrier for 3s, blocks incoming damage from front
        {
          const duration = 3000;
          
          // Apply front shield effect (provides damage reduction from frontal attacks)
          this.effectSystem.applyEffect(
            EffectType.FrontShield,
            player.id,
            player.id,
            duration,
            80 // 80% damage reduction from front
          );
          
          result.effectsApplied.push('front_shield');
          result.visualEffects!.push({
            type: VisualEffectType.BarrierWall,
            position: { ...player.position },
            direction: player.rotation,
            duration,
            color: '#0ce5f0',
            followPlayerId: player.id // Barrier follows the player
          });
          result.success = true;
        }
        break;

      case 2: // F: Shockwave Slam - AoE stun 4 tile radius, 30 damage
        {
          const radius = ABILITY_RANGES.SHOCKWAVE;
          const damage = 30;
          const stunDuration = 1500;
          
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          const hitTargets: EntityId[] = [];
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist <= radius) {
              // Apply stun
              this.effectSystem.applyEffect(
                EffectType.Stun,
                player.id,
                enemy.id,
                stunDuration,
                100
              );
              hitTargets.push(enemy.id);
            }
          }
          
          result.damage = damage;
          result.targetIds = hitTargets;
          result.effectsApplied.push('stun');
          result.visualEffects!.push({
            type: VisualEffectType.Shockwave,
            position: player.position,
            radius,
            duration: 500
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Overclock - 40% DR + fire rate for 5s
        {
          const duration = 5000;
          
          this.effectSystem.applyEffect(
            EffectType.DamageReduction,
            player.id,
            player.id,
            duration,
            40
          );
          
          this.effectSystem.applyEffect(
            EffectType.FireRateBoost,
            player.id,
            player.id,
            duration,
            40
          );
          
          result.effectsApplied.push('damage_reduction', 'fire_rate_boost');
          result.visualEffects!.push({
            type: VisualEffectType.Shield,
            position: player.position,
            duration,
            color: '#ff0000'
          });
          result.success = true;
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // SPECTRE - Recon
  // Passive: X-ray 3 tiles | Q: Reveal 5 tiles | E: Hologram decoy
  // -------------------------------------------------------------------------

  private executeSpectreAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: See through walls 3 tiles - handled in rendering
        break;

      case 1: // Q: Cloak - invisibility 2s (existing)
        {
          const duration = 2000;
          this.effectSystem.applyEffect(
            EffectType.Cloak,
            player.id,
            player.id,
            duration,
            100
          );
          result.effectsApplied.push('cloak');
          result.visualEffects!.push({
            type: VisualEffectType.Cloak,
            position: player.position,
            duration
          });
          result.success = true;
        }
        break;

      case 2: // F: Reveal - reveal all enemies within 5 tiles for 3s
        {
          const radius = ABILITY_RANGES.REVEAL;
          const duration = 3000;
          
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          const revealedTargets: EntityId[] = [];
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist <= radius) {
              this.effectSystem.applyEffect(
                EffectType.Revealed,
                player.id,
                enemy.id,
                duration,
                100
              );
              revealedTargets.push(enemy.id);
            }
          }
          
          result.targetIds = revealedTargets;
          result.effectsApplied.push('revealed');
          result.visualEffects!.push({
            type: VisualEffectType.Reveal,
            position: player.position,
            radius,
            duration: 500
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Shadow Dive - teleport behind enemy
        {
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          if (enemies.length === 0) break;
          
          // Find closest enemy
          let target: Player | null = null;
          let closestDist = Infinity;
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist < closestDist) {
              closestDist = dist;
              target = enemy;
            }
          }
          
          if (target) {
            const behindDir = Vec2.fromAngle(target.rotation + Math.PI);
            const behindPos = Vec2.add(target.position, Vec2.mul(behindDir, 50));
            
            result.teleportTo = behindPos;
            result.visualEffects!.push(
              { type: VisualEffectType.Blink, position: player.position, duration: 300 },
              { type: VisualEffectType.Blink, position: behindPos, duration: 300 }
            );
            result.success = true;
          }
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // MEDIC (Mira) - Support
  // Passive: Proximity heal | Q: Heal burst 50 HP | E: Revive
  // -------------------------------------------------------------------------

  private executeMedicAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: Heal allies 3 HP/s in 2 tile radius - handled in update
        break;

      case 1: // Q: Nano-Heal Field - healing zone for 4s
        {
          const radius = tilesToPixels(3);
          const duration = 4000;
          const healPerTick = 10;
          const tickInterval = 500;
          
          this.effectSystem.createZone(
            EffectType.Regen,
            player.id,
            player.position,
            radius,
            duration,
            tickInterval,
            healPerTick,
            true, // affects allies
            false, // doesn't affect enemies
            ZoneVisualType.HealingField
          );
          
          result.zones = [{
            id: generateId('zone'),
            type: ZoneVisualType.HealingField,
            position: { ...player.position },
            radius,
            duration
          }];
          result.visualEffects!.push({
            type: VisualEffectType.HealingField,
            position: player.position,
            radius,
            duration,
            color: '#00ff88'
          });
          result.success = true;
        }
        break;

      case 2: // F: Shield Burst - give 30 temp shield to nearby allies
        {
          const radius = ABILITY_RANGES.HEAL_BURST;
          const shieldAmount = 30;
          const duration = 3000;
          
          const allies = this.getAllies(player.team, player.id).filter(a => a.alive);
          const shieldedTargets: EntityId[] = [];
          
          for (const ally of allies) {
            const dist = Vec2.distance(player.position, ally.position);
            if (dist <= radius) {
              this.effectSystem.applyEffect(
                EffectType.Shield,
                player.id,
                ally.id,
                duration,
                shieldAmount
              );
              shieldedTargets.push(ally.id);
            }
          }
          
          // Also shield self
          this.effectSystem.applyEffect(
            EffectType.Shield,
            player.id,
            player.id,
            duration,
            shieldAmount
          );
          
          result.targetIds = shieldedTargets;
          result.effectsApplied.push('shield');
          result.visualEffects!.push({
            type: VisualEffectType.HealBurst,
            position: player.position,
            radius,
            duration: 500,
            color: '#00aaff'
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Resync - instant revive ally with 50% HP
        {
          // Find closest dead ally
          const allies = this.getAllies(player.team, player.id).filter(a => !a.alive);
          if (allies.length === 0) break;
          
          let target: Player | null = null;
          let closestDist = tilesToPixels(5); // Max revive range
          
          for (const ally of allies) {
            const dist = Vec2.distance(player.position, ally.position);
            if (dist < closestDist) {
              closestDist = dist;
              target = ally;
            }
          }
          
          if (target) {
            result.targetIds = [target.id];
            result.healing = target.maxHealth * 0.5;
            result.effectsApplied.push('revive');
            result.visualEffects!.push({
              type: VisualEffectType.HealBurst,
              position: target.position,
              duration: 1000,
              color: '#ffff00'
            });
            result.success = true;
          }
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // HAVOC (Spark) - Assault
  // Passive: +10% fire rate | Q: Grenade | E: Minigun mode
  // -------------------------------------------------------------------------

  private executeHavocAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: +10% fire rate - handled in weapon system
        break;

      case 1: // Q: Plasma Fire Bottle - burning zone
        {
          const throwDistance = ABILITY_RANGES.GRENADE;
          const direction = Vec2.fromAngle(player.rotation);
          const targetPos = Vec2.add(player.position, Vec2.mul(direction, throwDistance));
          
          // Check for wall collision
          const hit = this.raycast(player.position, direction, throwDistance, [player.id]);
          const landingPos = hit ? hit.point : targetPos;
          
          const radius = tilesToPixels(2);
          const duration = 4000;
          const damagePerTick = 15;
          const tickInterval = 500;
          
          this.effectSystem.createZone(
            EffectType.Burn,
            player.id,
            landingPos,
            radius,
            duration,
            tickInterval,
            damagePerTick,
            false, // doesn't affect allies
            true,  // affects enemies
            ZoneVisualType.FireZone
          );
          
          result.zones = [{
            id: generateId('zone'),
            type: ZoneVisualType.FireZone,
            position: landingPos,
            radius,
            duration
          }];
          result.visualEffects!.push(
            { type: VisualEffectType.Grenade, position: player.position, targetPosition: landingPos, duration: 500 },
            { type: VisualEffectType.Explosion, position: landingPos, radius, duration: 300 }
          );
          result.success = true;
        }
        break;

      case 2: // F: Thermal Burst - clear smoke/holos + reveal
        {
          const radius = tilesToPixels(4);
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist <= radius) {
              // Remove cloak effects
              this.effectSystem.removeEffectsByType(enemy.id, EffectType.Cloak);
              // Apply revealed
              this.effectSystem.applyEffect(
                EffectType.Revealed,
                player.id,
                enemy.id,
                2000,
                100
              );
            }
          }
          
          result.effectsApplied.push('revealed');
          result.visualEffects!.push({
            type: VisualEffectType.Shockwave,
            position: player.position,
            radius,
            duration: 300,
            color: '#ff8800'
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Thermal Overdrive - massive fire wave
        {
          const radius = tilesToPixels(5);
          const damage = 60;
          
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          const hitTargets: EntityId[] = [];
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist <= radius) {
              // Apply burn DoT
              this.effectSystem.applyEffect(
                EffectType.Burn,
                player.id,
                enemy.id,
                3000,
                10,
                500 // tick every 500ms
              );
              hitTargets.push(enemy.id);
            }
          }
          
          result.damage = damage;
          result.targetIds = hitTargets;
          result.effectsApplied.push('burn');
          result.visualEffects!.push({
            type: VisualEffectType.Explosion,
            position: player.position,
            radius,
            duration: 800,
            color: '#ff4400'
          });
          result.success = true;
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // CIPHER (Byte) - Tech
  // Passive: Hack faster | Q: Disable abilities | E: Deploy turret
  // -------------------------------------------------------------------------

  private executeCipherAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: Hack 50% faster - handled in interaction system
        break;

      case 1: // Q: EMP Pulse - disable + slow enemies in area
        {
          const radius = tilesToPixels(4);
          const duration = 1500;
          
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          const hitTargets: EntityId[] = [];
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist <= radius) {
              // Silence (can't use abilities)
              this.effectSystem.applyEffect(
                EffectType.Silence,
                player.id,
                enemy.id,
                duration,
                100
              );
              // Slow
              this.effectSystem.applyEffect(
                EffectType.Slow,
                player.id,
                enemy.id,
                duration,
                30 // 30% slow
              );
              hitTargets.push(enemy.id);
            }
          }
          
          result.targetIds = hitTargets;
          result.effectsApplied.push('silence', 'slow');
          result.visualEffects!.push({
            type: VisualEffectType.Shockwave,
            position: player.position,
            radius,
            duration: 400,
            color: '#00ffff'
          });
          result.success = true;
        }
        break;

      case 2: // F: Hack Trap - slowing data field
        {
          const direction = Vec2.fromAngle(player.rotation);
          const trapPos = Vec2.add(player.position, Vec2.mul(direction, 60));
          
          const radius = tilesToPixels(2);
          const duration = 5000;
          
          this.effectSystem.createZone(
            EffectType.Slow,
            player.id,
            trapPos,
            radius,
            duration,
            500,
            40, // 40% slow
            false,
            true,
            ZoneVisualType.SlowField
          );
          
          result.zones = [{
            id: generateId('zone'),
            type: ZoneVisualType.SlowField,
            position: trapPos,
            radius,
            duration
          }];
          result.visualEffects!.push({
            type: VisualEffectType.TurretSpawn,
            position: trapPos,
            duration: 300,
            color: '#00aaff'
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Deploy Turret - automated turret for 10s
        {
          const direction = Vec2.fromAngle(player.rotation);
          const turretPos = Vec2.add(player.position, Vec2.mul(direction, 50));
          
          const turret: Turret = {
            id: generateId('turret'),
            ownerId: player.id,
            ownerTeam: player.team,
            position: turretPos,
            rotation: player.rotation,
            health: 100,
            maxHealth: 100,
            range: ABILITY_RANGES.TURRET_RANGE,
            damage: 20,
            fireRate: 1000, // 1 shot per second
            lastFireTime: 0,
            spawnTime: this.getTime(),
            duration: 10000,
            targetId: null,
          };
          
          this.turrets.set(turret.id, turret);
          
          result.visualEffects!.push({
            type: VisualEffectType.TurretSpawn,
            position: turretPos,
            duration: 500
          });
          result.success = true;
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // VENOM (Glyph) - Saboteur
  // Passive: Poison on melee | Q: Toxic cloud | E: Plague (chain poison)
  // -------------------------------------------------------------------------

  private executeVenomAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: Poison on melee - handled in melee damage
        break;

      case 1: // Q: Shock Drone (repurposed as Toxic Spit - ranged poison)
        {
          const range = ABILITY_RANGES.MEDIUM;
          const direction = Vec2.fromAngle(player.rotation);
          
          // Find first enemy in line
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          let target: Player | null = null;
          
          for (const enemy of enemies) {
            const toEnemy = Vec2.sub(enemy.position, player.position);
            const dist = Vec2.length(toEnemy);
            if (dist > range) continue;
            
            const dot = Vec2.dot(Vec2.normalize(toEnemy), direction);
            if (dot > 0.9) { // Within ~25 degree cone
              target = enemy;
              break;
            }
          }
          
          if (target) {
            this.effectSystem.applyEffect(
              EffectType.Poison,
              player.id,
              target.id,
              3000,
              8, // 8 damage per tick
              500
            );
            result.targetIds = [target.id];
            result.effectsApplied.push('poison');
          }
          
          result.visualEffects!.push({
            type: VisualEffectType.DashSlash,
            position: player.position,
            direction: player.rotation,
            duration: 300,
            color: '#00ff00'
          });
          result.success = true;
        }
        break;

      case 2: // F: Toxic Cloud - poison zone
        {
          const direction = Vec2.fromAngle(player.rotation);
          const cloudPos = Vec2.add(player.position, Vec2.mul(direction, ABILITY_RANGES.SHORT));
          
          const radius = ABILITY_RANGES.POISON_CLOUD;
          const duration = 5000;
          
          this.effectSystem.createZone(
            EffectType.Poison,
            player.id,
            cloudPos,
            radius,
            duration,
            500,
            15, // damage per tick
            false,
            true,
            ZoneVisualType.PoisonCloud
          );
          
          result.zones = [{
            id: generateId('zone'),
            type: ZoneVisualType.PoisonCloud,
            position: cloudPos,
            radius,
            duration
          }];
          result.visualEffects!.push({
            type: VisualEffectType.PoisonCloud,
            position: cloudPos,
            radius,
            duration,
            color: '#44ff44'
          });
          result.success = true;
        }
        break;

      case 3: // Ultimate: Nano Swarm / Plague - chain poison
        {
          const range = ABILITY_RANGES.MEDIUM;
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          
          // Find closest enemy to start the chain
          let firstTarget: Player | null = null;
          let closestDist = range;
          
          for (const enemy of enemies) {
            const dist = Vec2.distance(player.position, enemy.position);
            if (dist < closestDist) {
              closestDist = dist;
              firstTarget = enemy;
            }
          }
          
          if (firstTarget) {
            // Apply strong poison to first target
            this.effectSystem.applyEffect(
              EffectType.Poison,
              player.id,
              firstTarget.id,
              6000,
              10,
              500
            );
            
            // Chain to nearby enemies
            const chainRadius = tilesToPixels(2);
            const infected: EntityId[] = [firstTarget.id];
            
            for (const enemy of enemies) {
              if (enemy.id === firstTarget.id) continue;
              const dist = Vec2.distance(firstTarget.position, enemy.position);
              if (dist <= chainRadius && infected.length < 4) {
                this.effectSystem.applyEffect(
                  EffectType.Poison,
                  player.id,
                  enemy.id,
                  6000,
                  8,
                  500
                );
                infected.push(enemy.id);
              }
            }
            
            result.targetIds = infected;
            result.effectsApplied.push('poison');
            result.visualEffects!.push({
              type: VisualEffectType.PoisonCloud,
              position: firstTarget.position,
              radius: chainRadius,
              duration: 1000,
              color: '#00ff00'
            });
            result.success = true;
          }
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // AEGIS (Zero) - Guardian
  // Passive: Shield ally on spawn | Q: Barrier wall | E: Dome shield
  // -------------------------------------------------------------------------

  private executeAegisAbility(player: Player, abilityIndex: number): AbilityResult {
    const result: AbilityResult = { success: false, effectsApplied: [], visualEffects: [] };

    switch (abilityIndex) {
      case 0: // Passive: Nearest ally gets +25 shield on spawn - handled in spawn
        break;

      case 1: // Q: Mark Target - tag for bonus damage
        {
          // Very long range for a sniper ability
          const range = ABILITY_RANGES.VERY_LONG * 2; // ~768 pixels - very far
          const direction = Vec2.fromAngle(player.rotation);
          
          console.log(`[Zero] Mark Target activated - scanning for enemies...`);
          
          // Find enemy in aim direction (very forgiving targeting)
          const enemies = this.getEnemies(player.team).filter(e => e.alive);
          let target: Player | null = null;
          let bestDot = 0.5; // Minimum dot product (about 60 degrees cone - very forgiving)
          let closestDist = Infinity;
          
          console.log(`[Zero] Found ${enemies.length} alive enemies to check`);
          
          for (const enemy of enemies) {
            const toEnemy = Vec2.sub(enemy.position, player.position);
            const dist = Vec2.length(toEnemy);
            
            if (dist > range) {
              console.log(`[Zero] Enemy ${enemy.name} too far: ${dist.toFixed(0)} > ${range}`);
              continue;
            }
            
            const normalizedToEnemy = Vec2.normalize(toEnemy);
            const dot = Vec2.dot(normalizedToEnemy, direction);
            
            console.log(`[Zero] Enemy ${enemy.name}: dist=${dist.toFixed(0)}, dot=${dot.toFixed(2)} (need >${bestDot})`);
            
            // Find the closest target within cone
            if (dot > bestDot && dist < closestDist) {
              closestDist = dist;
              target = enemy;
            }
          }
          
          if (target) {
            this.effectSystem.applyEffect(
              EffectType.Marked,
              player.id,
              target.id,
              5000,
              20 // 20% bonus damage
            );
            result.targetIds = [target.id];
            result.effectsApplied.push('marked');
            
            console.log(`[Zero] ✓ MARKED: ${target.name} for 5 seconds (+20% damage)`);
          } else {
            console.log(`[Zero] ✗ No target found. Make sure you're aiming at an enemy within range.`);
          }
          
          result.success = true;
        }
        break;

      case 2: // F: Dash Back - quick backward escape
        {
          const dashDistance = ABILITY_RANGES.BLINK; // Same distance as blink
          // Dash BACKWARD (opposite of facing direction)
          const backwardDirection = Vec2.fromAngle(player.rotation + Math.PI);
          const targetPos = Vec2.add(player.position, Vec2.mul(backwardDirection, dashDistance));
          
          // Check for wall collision along the path
          const hit = this.raycast(player.position, backwardDirection, dashDistance, [player.id]);
          const finalPos = hit ? Vec2.sub(hit.point, Vec2.mul(backwardDirection, 20)) : targetPos;
          
          result.teleportTo = finalPos;
          result.visualEffects!.push(
            { type: VisualEffectType.Blink, position: player.position, duration: 300 },
            { type: VisualEffectType.BlinkTrail, position: player.position, targetPosition: finalPos, duration: 200 }
          );
          result.success = true;
          console.log(`[Zero] Dash Back executed`);
        }
        break;

      case 3: // Ultimate: Dome Shield - protective dome for team
        {
          const radius = ABILITY_RANGES.DOME_SHIELD;
          const duration = 5000;
          
          // Apply damage reduction to all allies in dome
          const allies = this.getAllies(player.team);
          for (const ally of allies) {
            const dist = Vec2.distance(player.position, ally.position);
            if (dist <= radius) {
              this.effectSystem.applyEffect(
                EffectType.DamageReduction,
                player.id,
                ally.id,
                duration,
                70 // 70% damage reduction
              );
            }
          }
          
          // Also protect self
          this.effectSystem.applyEffect(
            EffectType.DamageReduction,
            player.id,
            player.id,
            duration,
            70
          );
          
          result.effectsApplied.push('damage_reduction');
          result.visualEffects!.push({
            type: VisualEffectType.DomeShield,
            position: player.position,
            radius,
            duration,
            color: '#4488ff'
          });
          result.success = true;
        }
        break;
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // TURRET UPDATE
  // -------------------------------------------------------------------------

  updateTurrets(_dt: number): Array<{ turretId: string; targetId: EntityId; damage: number; position: Vector2 }> {
    const shots: Array<{ turretId: string; targetId: EntityId; damage: number; position: Vector2 }> = [];
    const now = this.getTime();

    for (const [id, turret] of this.turrets) {
      // Check expiration
      if (now >= turret.spawnTime + turret.duration) {
        this.turrets.delete(id);
        continue;
      }

      // Find target
      const enemies = this.getEnemies(turret.ownerTeam).filter(e => e.alive);
      let target: Player | null = null;
      let closestDist = turret.range;

      for (const enemy of enemies) {
        const dist = Vec2.distance(turret.position, enemy.position);
        if (dist < closestDist) {
          closestDist = dist;
          target = enemy;
        }
      }

      if (target) {
        turret.targetId = target.id;
        turret.rotation = Vec2.angle(Vec2.sub(target.position, turret.position));

        // Fire if ready
        if (now >= turret.lastFireTime + turret.fireRate) {
          turret.lastFireTime = now;
          shots.push({
            turretId: id,
            targetId: target.id,
            damage: turret.damage,
            position: turret.position,
          });
        }
      } else {
        turret.targetId = null;
      }
    }

    return shots;
  }

  getTurrets(): Turret[] {
    return Array.from(this.turrets.values());
  }

  clearTurrets(): void {
    this.turrets.clear();
  }

  damageTurret(turretId: string, damage: number): boolean {
    const turret = this.turrets.get(turretId);
    if (!turret) return false;

    turret.health -= damage;
    if (turret.health <= 0) {
      this.turrets.delete(turretId);
      return true; // Destroyed
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // MINIGUN MODE
  // -------------------------------------------------------------------------

  isInMinigunMode(playerId: EntityId): boolean {
    const endTime = this.activeMiniguns.get(playerId);
    if (!endTime) return false;
    return this.getTime() < endTime;
  }

  activateMinigunMode(playerId: EntityId, duration: number): void {
    this.activeMiniguns.set(playerId, this.getTime() + duration);
  }

  // -------------------------------------------------------------------------
  // CLEANUP
  // -------------------------------------------------------------------------

  clear(): void {
    this.turrets.clear();
    this.activeMiniguns.clear();
  }
}
